/**
 * The instructions.
 *
 * They exist because the game asks for things no first-time player can guess:
 * that `E` is a swig from the bottle and nothing else, that the window in the
 * pirtti has to be punched out before it is a way anywhere, that jumping is a
 * cost rather than a move, that the last corridor is shut until there is a
 * full tuoppi in his hand, and that the dose picks which leaderboard you are
 * on. None of that fits in a HUD footer, and all of it is the difference
 * between a 45 s run and a 90 s one.
 *
 * Kept honest on purpose: the tips describe the systems as they actually are
 * (see `config/gameplay.ts` and `config/beer.ts`), not as encouragement.
 */

import { el } from './dom';
import { MenuScreen } from './MenuScreen';

const GOAL =
  'Herää Einon pirtin sängystä, ulos pirtistä, parkkipaikan läpi autojen välistä, ' +
  'Hannun paljun ohi juhlanavettaan ja omalle paikalle istumaan. Herätessä kello laskee ' +
  'miinuksesta nollaan. Nollassa pääset liikkeelle. Istu koneelle F:llä ja klikkaa ruudun ' +
  'näppäimistöltä vasemmassa ylänurkassa näkyvä nelinumeroinen PIN-koodi. Näppäimistö ' +
  'huojuu päihtymyksen mukaan. ⌫ korjaa numeron. Kello pysähtyy oikeaan koodiin, ' +
  'ja Windows-työpöytä avautuu.';

const CONTROLS: readonly [string, string][] = [
  ['W A S D', 'Liike. Myös nuolinäppäimet.'],
  ['Hiiri', 'Katselu. Peli ottaa hiiren, ESC antaa sen takaisin.'],
  ['Space', 'Hyppy. Maksaa vauhtia - ks. vinkit.'],
  [
    'F',
    'Kaikki mihin kädellä ylletään: ovi, klapi, pirtin ikkuna, tuoppi pinosta, ' +
      'baarin hana ja oma tuoli.',
  ],
  [
    'E',
    'Huikka pullosta. Alkoholi +0,05 ‰. Vauhtikerroin +0,15, yhteensä enintään +0,30. ' +
      'Yhden huikan vauhtibonus hiipuu noin 1,9 sekunnissa. ' +
      'Heilauttaa vuorotellen vasemmalle ja oikealle. ' +
      'Toimii missä tahansa, myös ovensuussa.',
  ],
  ['1 2 3', 'Aamupala muurikalta. Laatikko, jossa on lihaa - ei vegeä.'],
  ['R', 'Uusi yritys, heti.'],
  ['ESC', 'Tauko ja takaisin valikkoon.'],
  ['F3', 'Debug-tiedot.'],
  ['F4', 'Colliderit näkyviin.'],
];

const TIPS: readonly string[] = [
  'Vauhtimittari kasvaa puhtaasta juoksusta ja leikkaantuu hypystä, seinästä ja ' +
    'Hannun kädestä. Nopein linja on harvoin se, jossa hypitään eniten.',
  'Huikka pullosta antaa hetkeksi lisävauhtia ja heilauttaa vuorotellen vasemmalle ' +
    'ja oikealle, ensimmäisellä huikalla vasemmalle. Heilautuksen voima ja kesto ' +
    'vaihtelevat. Juo suoralla, älä ovessa.',
  'Makuuhuoneen ikkunassa on lasi. Sen läpi ei mennä ennen kuin sen on lyönyt ' +
    'rikki - ja rikkominen viiltää kädet, mikä maksaa kolme sekuntia vauhtia. ' +
    'Käytävän kautta pääsee ulos ilman kumpaakaan.',
  'Käytävän neljästä sängystä vain ensimmäinen on niin matala, että sen päälle ' +
    'pääsee juoksemalla. Muut ovat korkeammalla kuin hyppy yltää, joten niiden ' +
    'päälle pääsee vain edelliseltä sängyltä - eli koko käytävän voi juosta ' +
    'suoraan sänkyjen päältä, jos ehtii ensimmäiselle. Alas pudonnut ei pääse ' +
    'enää takaisin ylös.',
  'Juhlasalin porukka ei päästä omalle paikalle ennen kuin kädessä on täysi tuoppi. ' +
    'Molemmat puoliskot ovat oikealla salin ensimmäisillä metreillä: tyhjät tuopit ovat ' +
    'pinossa baaritiskin päässä olevalla pikkupöydällä ja hanat itse tiskillä. F nostaa ' +
    'lasin pinosta ja F oikeanpuoleisen PANIMOSIMA-hanan alla täyttää tuopin simalla.',
  'Tuopin täyttyminen kestää kaksi sekuntia, eikä se täyty kaukaa: hanalta pitää ' +
    'pysyä käden mitan päässä koko ajan. Kesken jäänyt tuoppi ei tyhjene, joten ' +
    'takaisin voi tulla - mutta porukka haluaa nähdä täyden lasin.',
  'Promillet säilyttävät vauhtia mutta leventävät käännöksiä ja pidentävät jarrutusta. ' +
    'Ennakoi mutkat: suoraan juostessa hahmo ei huojahtele sivuille.',
  'Promillet ja muut päihteet vaihtavat tulostaulun: jokainen annos on oma sarjansa, ' +
    'ja niiden muuttaminen aloittaa yrityksen alusta.',
];

export function createHelpScreen(onClose: () => void): MenuScreen {
  const screen = new MenuScreen('OHJEET', 'Mitä täällä tehdään ja millä.', onClose, 'SELVÄ');

  const goal = el('section', 'help__section');
  goal.append(el('h3', 'settings__title', 'Tavoite'), el('p', 'help__body', GOAL));

  const controls = el('section', 'help__section');
  const keys = el('dl', 'help__keys');
  for (const [key, what] of CONTROLS) {
    const term = el('dt');
    term.appendChild(el('kbd', undefined, key));
    keys.append(term, el('dd', undefined, what));
  }
  controls.append(el('h3', 'settings__title', 'Kontrollit'), keys);

  const tips = el('section', 'help__section');
  const list = el('ul', 'help__tips');
  for (const tip of TIPS) list.appendChild(el('li', undefined, tip));
  tips.append(el('h3', 'settings__title', 'Vinkit'), list);

  screen.body.append(goal, controls, tips);
  return screen;
}
